import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader as TableHeaderShadCN,
  TableRow,
} from '@frontend/shadcn/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { ArrowDownToLineIcon, ChevronLeftIcon, ChevronRightIcon } from '@revfluence/fresh-icons/solid';
import { GetSocialAnalyticsPostsQuery } from '@frontend/gql/social/graphql';
import { Card } from '@frontend/shadcn/components/ui/card';
import { SearchInput } from '@frontend/app/refresh-components';
import { SocialMediaThumbnail } from '../../../components/thumbnail/SocialMediaThumbnail';
import { formatNumberDefault } from '../../../utils/formatNumber';
import MembersCard from '../members-card';
import ReachCard from '../reach-card';
import EngagementCard from '../engagement-card';
import ImpressionsCard from '../impressions-card';
import PostsCard from '../post-card';
import TMVCard from '../tmv-card';
import {
  TableHeader,
  TableHeaderSection,
  TableHeaderLayout,
  TableHeaderLabel,
  TableHeaderIconButton,
  TableHeaderPagination,
} from '../table-header';

const THUMBNAIL_LIMIT = 20;

interface PostsTabProps {
  data: GetSocialAnalyticsPostsQuery | undefined;
  isLoading: boolean;
  isPartialLoading: boolean;
}

export const PostsTab = ({ data, isLoading, isPartialLoading }: PostsTabProps) => {
  const posts = data?.client?.socialAnalytics?.posts?.results || [];
  const totalInsights = data?.client?.socialAnalytics?.totalInsights;
  if (isLoading && !isPartialLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4">
        <PostsCard data={totalInsights} />
        <MembersCard data={totalInsights} />
        <ImpressionsCard data={totalInsights} />
        <EngagementCard data={totalInsights} />
        <ReachCard data={totalInsights} />
        <TMVCard data={totalInsights} />
      </section>
      <Card className="mx-4 mb-4" aria-label="Members table">
        <TableHeader>
          <TableHeaderLayout>
            <TableHeaderSection>
              <TableHeaderLabel>
                <div className="flex items-center gap-2">
                  <span aria-live="polite">20 posts</span>
                </div>
              </TableHeaderLabel>
            </TableHeaderSection>
            <TableHeaderSection>
              <SearchInput
                searchText=""
                setSearchText={() => {
                  // TODO: Implement search
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <TableHeaderIconButton data-dd-action-name="export-members-data">
                    <div className="flex items-center">
                      <ArrowDownToLineIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                  </TableHeaderIconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-dd-action-name="export-current-view"
                    aria-label="Export current view of member data"
                  >
                    Export current view
                  </DropdownMenuItem>
                  <DropdownMenuItem data-dd-action-name="export-full-data" aria-label="Export all member data fields">
                    Export all fields
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* <ColumnDialog>
                <TableHeaderIconButton
                  data-dd-action-name="open-column-selector"
                  aria-label="Select columns to display"
                >
                  <ColumnsIconV3 size={16} aria-hidden="true" />
                </TableHeaderIconButton>
              </ColumnDialog> */}
              <nav aria-label="Table navigation" className="flex items-center">
                <TableHeaderIconButton data-dd-action-name="members-table-previous-page" aria-label="Previous page">
                  <ChevronLeftIcon aria-hidden="true" />
                </TableHeaderIconButton>
                <TableHeaderPagination currentPage={0} limit={0} totalCount={0} />
                <TableHeaderIconButton data-dd-action-name="members-table-next-page" aria-label="Next page">
                  <ChevronRightIcon aria-hidden="true" />
                </TableHeaderIconButton>
              </nav>
            </TableHeaderSection>
          </TableHeaderLayout>
        </TableHeader>
        <Table>
          <TableHeaderShadCN>
            <TableRow>
              <TableHead>Preview</TableHead>
              <TableHead>Network</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Member</TableHead>
              <TableHead>Reach</TableHead>
              <TableHead>Impressions</TableHead>
              <TableHead>Engagements</TableHead>
              <TableHead>Likes</TableHead>
              <TableHead>Comments</TableHead>
              <TableHead>Views</TableHead>
              <TableHead>Saves</TableHead>
              <TableHead>Shares</TableHead>
            </TableRow>
          </TableHeaderShadCN>
          <TableBody>
            {posts.map((post, index) => (
              <TableRow key={post.entityId}>
                <TableCell className="p-2">
                  <div className="w-[100px] h-[100px] overflow-hidden flex items-center justify-center bg-gray-50">
                    {index < THUMBNAIL_LIMIT && (
                      <SocialMediaThumbnail
                        url={post.postPermalink}
                        width="100%"
                        height="100%"
                        className="rounded-md object-contain"
                        alt={`${post.member?.name || 'Unknown'} - ${post.entityType} on ${post.entityNetwork}`}
                        loadThumbnail={index < THUMBNAIL_LIMIT}
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell>{post.entityNetwork}</TableCell>
                <TableCell>{post.entityType}</TableCell>
                <TableCell>
                  <a
                    href={post.postPermalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {post.member?.name}
                  </a>
                </TableCell>
                <TableCell>{formatNumberDefault(post.total?.reach)}</TableCell>
                <TableCell>{formatNumberDefault(post.total?.impressions)}</TableCell>
                <TableCell>{formatNumberDefault(post.total?.engagements)}</TableCell>
                <TableCell>{formatNumberDefault(post.total?.likes)}</TableCell>
                <TableCell>{formatNumberDefault(post.total?.comments)}</TableCell>
                <TableCell>{formatNumberDefault(post.total?.views)}</TableCell>
                <TableCell>{formatNumberDefault(post.total?.saves)}</TableCell>
                <TableCell>{formatNumberDefault(post.total?.shares)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
};
